import * as React from 'react';

// Sensor configurations with optimal ranges
const SENSOR_CONFIG = {
  ph: { id: 'ph', label: 'pH Level', shortLabel: 'pH', unit: '', min: 0, max: 14, optimalMin: 5.5, optimalMax: 6.5, color: '#8b5cf6', baseValue: 6.2 },
  ec: { id: 'ec', label: 'Electrical Conductivity', shortLabel: 'EC', unit: 'mS/cm', min: 0, max: 5, optimalMin: 1.2, optimalMax: 2.2, color: '#06b6d4', baseValue: 1.8 },
  tds: { id: 'tds', label: 'Total Dissolved Solids', shortLabel: 'TDS', unit: 'ppm', min: 0, max: 2000, optimalMin: 600, optimalMax: 1000, color: '#eab308', baseValue: 890 },
  waterTemp: { id: 'waterTemp', label: 'Water Temperature', shortLabel: 'Water Temp', unit: '°F', min: 50, max: 90, optimalMin: 65, optimalMax: 72, color: '#14b8a6', baseValue: 68 },
  orp: { id: 'orp', label: 'Oxidation Reduction', shortLabel: 'ORP', unit: 'mV', min: 0, max: 700, optimalMin: 300, optimalMax: 500, color: '#a855f7', baseValue: 320 },
  airTemp: { id: 'airTemp', label: 'Air Temperature', shortLabel: 'Air Temp', unit: '°F', min: 60, max: 95, optimalMin: 70, optimalMax: 80, color: '#ef4444', baseValue: 75 },
  humidity: { id: 'humidity', label: 'Relative Humidity', shortLabel: 'Humidity', unit: '%', min: 20, max: 90, optimalMin: 55, optimalMax: 70, color: '#3b82f6', baseValue: 62 },
  co2: { id: 'co2', label: 'Carbon Dioxide', shortLabel: 'CO₂', unit: 'ppm', min: 300, max: 2000, optimalMin: 800, optimalMax: 1200, color: '#22c55e', baseValue: 1100 },
  vpd: { id: 'vpd', label: 'Vapor Pressure Deficit', shortLabel: 'VPD', unit: 'kPa', min: 0, max: 2.5, optimalMin: 0.8, optimalMax: 1.4, color: '#f97316', baseValue: 1.2 },
  ppfd: { id: 'ppfd', label: 'PAR (PPFD)', shortLabel: 'PPFD', unit: 'µmol/m²/s', min: 0, max: 1500, optimalMin: 600, optimalMax: 1000, color: '#fbbf24', baseValue: 850 },
  dli: { id: 'dli', label: 'Daily Light Integral', shortLabel: 'DLI', unit: 'mol/m²/d', min: 0, max: 65, optimalMin: 30, optimalMax: 50, color: '#f59e0b', baseValue: 38.5 },
  waterLevel: { id: 'waterLevel', label: 'Water Level', shortLabel: 'Level', unit: '%', min: 0, max: 100, optimalMin: 60, optimalMax: 90, color: '#0ea5e9', baseValue: 78 },
};

// Calculate status color based on value relative to optimal range
export function getStatusColor(value, min, max, optimalMin, optimalMax) {
  if (value >= optimalMin && value <= optimalMax) {
    return '#22c55e'; // Green - optimal
  }

  // Calculate how far outside optimal range
  if (value < optimalMin) {
    const distanceFromOptimal = optimalMin - value;
    const totalLowRange = optimalMin - min;
    const ratio = distanceFromOptimal / totalLowRange;
    if (ratio < 0.3) return '#84cc16'; // Lime - slightly low
    if (ratio < 0.6) return '#eab308'; // Yellow - moderately low
    return '#ef4444'; // Red - critically low
  } else {
    const distanceFromOptimal = value - optimalMax;
    const totalHighRange = max - optimalMax;
    const ratio = distanceFromOptimal / totalHighRange;
    if (ratio < 0.3) return '#84cc16'; // Lime - slightly high
    if (ratio < 0.6) return '#f97316'; // Orange - moderately high
    return '#ef4444'; // Red - critically high
  }
}

// Calculate status text
export function getStatusText(value, optimalMin, optimalMax) {
  if (value >= optimalMin && value <= optimalMax) return 'Optimal';
  if (value < optimalMin) {
    const diff = optimalMin - value;
    const range = optimalMin;
    if (diff / range < 0.1) return 'Slightly Low';
    if (diff / range < 0.3) return 'Low';
    return 'Critical Low';
  } else {
    const diff = value - optimalMax;
    const range = optimalMax;
    if (diff / range < 0.1) return 'Slightly High';
    if (diff / range < 0.3) return 'High';
    return 'Critical High';
  }
}

const LiveSensorContext = React.createContext(null);

export function LiveSensorProvider({ children, updateInterval = 3000, historyLength = 50 }) {
  // Current sensor values
  const [sensorValues, setSensorValues] = React.useState(() => {
    const initial = {};
    Object.keys(SENSOR_CONFIG).forEach(key => {
      initial[key] = SENSOR_CONFIG[key].baseValue;
    });
    return initial;
  });

  // Historical data for charts (array of timestamped readings)
  const [sensorHistory, setSensorHistory] = React.useState(() => {
    const initial = {};
    const now = Date.now();
    Object.keys(SENSOR_CONFIG).forEach(key => {
      // Initialize with some historical data points
      initial[key] = [];
      for (let i = historyLength - 1; i >= 0; i--) {
        const config = SENSOR_CONFIG[key];
        const variation = (Math.random() - 0.5) * (config.max - config.min) * 0.1;
        initial[key].push({
          time: now - i * updateInterval,
          value: Math.max(config.min, Math.min(config.max, config.baseValue + variation)),
        });
      }
    });
    return initial;
  });

  // Trend data (direction and amount)
  const [sensorTrends, setSensorTrends] = React.useState(() => {
    const initial = {};
    Object.keys(SENSOR_CONFIG).forEach(key => {
      initial[key] = { direction: 'neutral', value: 0 };
    });
    return initial;
  });

  // Use ref to track latest values for history update
  const latestValuesRef = React.useRef(sensorValues);

  // Update sensors periodically
  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      setSensorValues(prev => {
        const newValues = { ...prev };
        const newTrends = {};

        Object.keys(SENSOR_CONFIG).forEach(key => {
          const config = SENSOR_CONFIG[key];
          const range = config.max - config.min;

          // Random walk with mean reversion towards base value
          const meanReversionStrength = 0.1;
          const randomWalk = (Math.random() - 0.5) * range * 0.05;
          const meanReversion = (config.baseValue - prev[key]) * meanReversionStrength;

          const newValue = Math.max(
            config.min,
            Math.min(config.max, prev[key] + randomWalk + meanReversion)
          );

          newValues[key] = Number(newValue.toFixed(2));

          // Calculate trend
          const diff = newValues[key] - prev[key];
          newTrends[key] = {
            direction: diff > 0.01 ? 'up' : diff < -0.01 ? 'down' : 'neutral',
            value: Math.abs(diff).toFixed(2),
          };
        });

        // Update ref for history
        latestValuesRef.current = newValues;
        setSensorTrends(newTrends);

        // Update history with new values
        setSensorHistory(prevHistory => {
          const newHistory = { ...prevHistory };
          Object.keys(SENSOR_CONFIG).forEach(key => {
            const history = [...prevHistory[key]];
            history.push({ time: now, value: newValues[key] });
            if (history.length > historyLength) {
              history.shift();
            }
            newHistory[key] = history;
          });
          return newHistory;
        });

        return newValues;
      });
    }, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval, historyLength]);

  // Get sensor with computed status
  const getSensorData = React.useCallback((sensorId) => {
    const config = SENSOR_CONFIG[sensorId];
    if (!config) return null;

    const value = sensorValues[sensorId];
    const trend = sensorTrends[sensorId];
    const history = sensorHistory[sensorId];

    return {
      ...config,
      value,
      trend,
      history,
      statusColor: getStatusColor(value, config.min, config.max, config.optimalMin, config.optimalMax),
      statusText: getStatusText(value, config.optimalMin, config.optimalMax),
    };
  }, [sensorValues, sensorTrends, sensorHistory]);

  // Get all sensor data
  const getAllSensorData = React.useCallback(() => {
    return Object.keys(SENSOR_CONFIG).map(key => getSensorData(key));
  }, [getSensorData]);

  const value = {
    sensorValues,
    sensorTrends,
    sensorHistory,
    sensorConfig: SENSOR_CONFIG,
    getSensorData,
    getAllSensorData,
    getStatusColor,
    getStatusText,
  };

  return (
    <LiveSensorContext.Provider value={value}>
      {children}
    </LiveSensorContext.Provider>
  );
}

export function useLiveSensors() {
  const context = React.useContext(LiveSensorContext);
  // Return null if not in LiveSensorProvider context (allows use outside provider)
  return context;
}

export { SENSOR_CONFIG };
export default LiveSensorContext;
